/* eslint-disable react/no-unescaped-entities */
/**
 * CompliancePolicyLibrary
 * -----------------------
 * Phase 22 Frontend Final — customer-facing UI for the Compliance
 * Policy Library.
 *
 * Sits below ``ContentRestrictionsSection`` inside the existing
 * "Content Restrictions & Review" area on the Business Profile page.
 * Never overwrites the manual restrictions above.
 *
 * All effective-policy state is authoritative on the server — this
 * component just orchestrates the workflow (upload → extract → human
 * review → approve/edit-and-approve/reject → deactivate) via the real
 * Phase 22 backend routes.
 *
 * Security posture:
 *   • Never puts GridFS ids, sha256, chunk ids, or system prompts into
 *     component state. Only uses the public projection.
 *   • Enforces no authorization client-side; the backend is the sole
 *     authoritative gate.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FileText, UploadCloud, Loader2, AlertTriangle, ShieldCheck,
  RefreshCw, Trash2, Download, X, Check, Edit3, XCircle, LockKeyhole,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import GlassCard from "@/components/GlassCard";

const ACCEPT = ".pdf,.docx,.txt";
const MAX_BYTES = 10 * 1024 * 1024;

const STATUS_LABELS = {
  // Document-level statuses
  pending: { label: "Uploading", tone: "info" },
  extracting: { label: "Extracting text", tone: "info" },
  ok: { label: "Text extracted", tone: "info" },
  empty: { label: "No text found", tone: "warn" },
  unsupported_encrypted: { label: "Password-protected PDF", tone: "warn" },
  extraction_failed: { label: "Extraction failed", tone: "error" },
  ready_for_review: { label: "Ready for review", tone: "success" },
  processing: { label: "Analyzing policy", tone: "info" },
  not_started: { label: "Ready to analyze", tone: "info" },
  superseded: { label: "Superseded", tone: "muted" },
  // Rule-level statuses
  pending_review: { label: "Pending review", tone: "info" },
  approved: { label: "Active", tone: "success" },
  rejected: { label: "Rejected", tone: "muted" },
  deactivated: { label: "Deactivated", tone: "muted" },
};

const CATEGORY_LABELS = {
  PROHIBITED_CLAIM: "Prohibited claim",
  REQUIRED_DISCLOSURE: "Required disclosure",
  REQUIRED_LANGUAGE: "Required language",
  RESTRICTED_TOPIC: "Restricted topic",
  ADDITIONAL_RESTRICTION: "Additional restriction",
  PLATFORM_RESTRICTION: "Platform restriction",
  APPROVAL_REQUIREMENT: "Approval requirement",
};

const UNSUPPORTED_ENFORCEMENT = new Set(["APPROVAL_REQUIREMENT"]);

function formatBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function StatusBadge({ status }) {
  const meta = STATUS_LABELS[status] || { label: status || "—", tone: "muted" };
  const cls = {
    info: "bg-blue-500/10 text-blue-300 border-blue-500/30",
    success: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    warn: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    error: "bg-red-500/10 text-red-300 border-red-500/30",
    muted: "bg-white/5 text-[color:var(--text-2)] border-white/10",
  }[meta.tone];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] ${cls}`}
      data-testid={`cpl-status-badge-${status}`}
    >
      {meta.label}
    </span>
  );
}

function EmptyState({ onBrowse }) {
  return (
    <div
      className="text-center py-10 border border-dashed border-white/10 rounded-lg"
      data-testid="cpl-empty-state"
    >
      <UploadCloud className="mx-auto h-8 w-8 text-[color:var(--text-3)]" />
      <div className="mt-3 text-sm text-[color:var(--text-1)]">
        No policy documents uploaded yet.
      </div>
      <div className="mt-1 text-xs text-[color:var(--text-3)]">
        Upload a PDF, DOCX, or TXT file to let Postmoro identify possible
        content rules for your review.
      </div>
      <Button
        onClick={onBrowse}
        variant="outline"
        className="mt-4"
        data-testid="cpl-empty-browse-btn"
      >
        <UploadCloud className="h-4 w-4 mr-1" /> Choose a file
      </Button>
    </div>
  );
}

function Dropzone({ onFile, uploading }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = useCallback((files) => {
    const f = files && files[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error("File is larger than 10 MB.");
      return;
    }
    onFile(f);
  }, [onFile]);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Upload compliance policy document"
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && !uploading) {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (uploading) return;
        handleFiles(e.dataTransfer.files);
      }}
      onClick={() => !uploading && inputRef.current?.click()}
      className={`border-2 border-dashed rounded-lg px-4 py-6 text-center cursor-pointer transition
        ${uploading ? "opacity-60 cursor-wait" : ""}
        ${dragOver
          ? "border-[color:var(--brand-primary)] bg-[color:var(--brand-primary)]/5"
          : "border-white/10 hover:border-white/20"
        }`}
      data-testid="cpl-dropzone"
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="cpl-file-input"
        disabled={uploading}
      />
      {uploading ? (
        <div className="flex items-center justify-center gap-2 text-sm text-[color:var(--text-1)]">
          <Loader2 className="h-4 w-4 animate-spin" />
          Uploading & extracting…
        </div>
      ) : (
        <>
          <UploadCloud className="mx-auto h-6 w-6 text-[color:var(--text-2)]" />
          <div className="mt-2 text-sm text-[color:var(--text-1)]">
            Drag & drop a policy document here, or click to browse
          </div>
          <div className="text-xs text-[color:var(--text-3)] mt-1">
            PDF, DOCX, or TXT — maximum 10 MB
          </div>
        </>
      )}
    </div>
  );
}

function TruncationBanner({ doc }) {
  const truncated = doc.extraction_truncated;
  const partial = doc.rule_extraction_partial;
  if (!truncated && !partial) return null;
  return (
    <div
      role="alert"
      className="mt-2 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
      data-testid={`cpl-partial-banner-${doc.document_id}`}
    >
      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
      <div>
        {truncated ? (
          <div>Only part of this document was analyzed.</div>
        ) : null}
        {partial ? (
          <div>Some sections could not be analyzed. Review the extracted rules carefully.</div>
        ) : null}
      </div>
    </div>
  );
}

function DocumentRow({
  doc, onExtract, onReview, onDownload, onDelete, onUploadNewVersion,
  onSupersede, existingIds,
}) {
  const isSuperseded = doc.status === "superseded";
  const statusForBadge = isSuperseded
    ? "superseded"
    : doc.rule_extraction_status && doc.rule_extraction_status !== "not_started"
      ? doc.rule_extraction_status
      : doc.text_extraction_status;
  const canExtract = doc.text_extraction_status === "ok"
    && doc.rule_extraction_status !== "processing";
  const canDelete = (doc.approved_rule_count || 0) === 0;

  return (
    <div
      className={`rounded-lg border border-white/10 p-3 ${isSuperseded ? "opacity-70" : ""}`}
      data-testid={`cpl-doc-row-${doc.document_id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <FileText className="h-4 w-4 text-[color:var(--text-2)] shrink-0" />
            <span
              className="text-sm text-[color:var(--text-1)] break-all"
              data-testid={`cpl-doc-name-${doc.document_id}`}
            >
              {doc.safe_filename || "Untitled"}
            </span>
            <span className="text-[11px] text-[color:var(--text-3)] uppercase">
              {doc.kind}
            </span>
            {doc.document_version > 1 ? (
              <Badge
                variant="outline"
                data-testid={`cpl-doc-version-${doc.document_id}`}
              >Version {doc.document_version}</Badge>
            ) : null}
          </div>
          <div className="mt-1 text-[11px] text-[color:var(--text-3)]">
            {formatBytes(doc.file_size)} · Uploaded {formatDate(doc.created_at)}
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <StatusBadge status={statusForBadge} />
            <span className="text-[11px] text-[color:var(--text-3)]">
              Pending: <b className="text-[color:var(--text-1)]">{doc.pending_rule_count || 0}</b>
              {" · "}
              Approved: <b className="text-[color:var(--text-1)]">{doc.approved_rule_count || 0}</b>
              {" · "}
              Rejected: <b className="text-[color:var(--text-1)]">{doc.rejected_rule_count || 0}</b>
            </span>
          </div>
          <TruncationBanner doc={doc} />
          {doc.text_extraction_status === "unsupported_encrypted" ? (
            <div
              className="mt-2 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
              data-testid={`cpl-encrypted-msg-${doc.document_id}`}
            >
              <LockKeyhole className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                This PDF is password-protected. Upload an unlocked copy so Postmoro can analyze it.
              </div>
            </div>
          ) : null}
          {doc.text_extraction_status === "extraction_failed"
            || doc.rule_extraction_status === "extraction_failed" ? (
            <div
              className="mt-2 flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200"
              data-testid={`cpl-failed-msg-${doc.document_id}`}
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                We couldn't finish analyzing this document. Delete and re-upload,
                or try analyzing again.
              </div>
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          {canExtract ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onExtract(doc)}
              data-testid={`cpl-extract-btn-${doc.document_id}`}
            >
              {doc.rule_extraction_status === "ready_for_review" ? "Re-analyze" : "Analyze"}
            </Button>
          ) : null}
          {(doc.candidate_rule_count || 0) > 0
            || (doc.approved_rule_count || 0) > 0 ? (
            <Button
              size="sm"
              onClick={() => onReview(doc)}
              data-testid={`cpl-review-btn-${doc.document_id}`}
            >
              Review rules
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDownload(doc)}
            aria-label={`Download ${doc.safe_filename}`}
            data-testid={`cpl-download-btn-${doc.document_id}`}
          >
            <Download className="h-4 w-4" />
          </Button>
          {!isSuperseded ? (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onUploadNewVersion(doc)}
                data-testid={`cpl-new-version-btn-${doc.document_id}`}
              >
                <RefreshCw className="h-4 w-4 mr-1" /> New version
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onDelete(doc, !canDelete)}
                aria-label={`Delete ${doc.safe_filename}`}
                data-testid={`cpl-delete-btn-${doc.document_id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ConfidenceHint({ v }) {
  if (typeof v !== "number") return null;
  const pct = Math.round(Math.max(0, Math.min(1, v)) * 100);
  return (
    <span className="text-[11px] text-[color:var(--text-3)]">
      Extraction confidence: {pct}%
    </span>
  );
}

function CandidateCard({
  rule, onApprove, onEditApprove, onReject, onDeactivate, disabled,
}) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(rule.rule_text || "");
  const isUnsupportedForEnforcement = UNSUPPORTED_ENFORCEMENT.has(rule.category);

  return (
    <div
      className="rounded-lg border border-white/10 p-3"
      data-testid={`cpl-candidate-${rule.rule_id}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="outline">{CATEGORY_LABELS[rule.category] || rule.category}</Badge>
        <StatusBadge status={rule.status} />
        {isUnsupportedForEnforcement ? (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-200 text-[11px]"
            data-testid={`cpl-unsupported-badge-${rule.rule_id}`}
          >
            Manual follow-up required
          </span>
        ) : null}
      </div>
      <div className="mt-2 text-sm text-[color:var(--text-1)] whitespace-pre-wrap break-words">
        {rule.status === "approved"
          ? rule.approved_rule_text || rule.rule_text
          : rule.rule_text}
      </div>
      {rule.status === "approved"
        && rule.approved_rule_text
        && rule.original_extracted_text
        && rule.approved_rule_text !== rule.original_extracted_text ? (
        <div className="mt-1 text-[11px] text-[color:var(--text-3)] italic">
          Original AI suggestion: {rule.original_extracted_text}
        </div>
      ) : null}
      {rule.source_excerpt ? (
        <div className="mt-2 text-[11px] text-[color:var(--text-3)]">
          <span className="uppercase tracking-wide mr-1">Source:</span>
          <span className="italic break-words">"{rule.source_excerpt}"</span>
          {rule.source_page ? (
            <span className="ml-1">· Page {rule.source_page}</span>
          ) : null}
          {rule.source_section ? (
            <span className="ml-1">· Section: {rule.source_section}</span>
          ) : null}
        </div>
      ) : null}
      {isUnsupportedForEnforcement ? (
        <div
          className="mt-2 text-[11px] text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1"
          data-testid={`cpl-unsupported-hint-${rule.rule_id}`}
        >
          Postmoro identified an approval requirement in this policy, but automated
          approval workflows are not currently enabled.
        </div>
      ) : null}

      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
        <ConfidenceHint v={rule.confidence} />
        <div className="flex gap-2 flex-wrap">
          {rule.status === "pending_review" ? (
            editing ? (
              <>
                <Textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  className="min-w-[280px] text-sm"
                  rows={2}
                  data-testid={`cpl-edit-textarea-${rule.rule_id}`}
                  aria-label="Edit approved rule text"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditing(false)}
                  disabled={disabled}
                  data-testid={`cpl-edit-cancel-${rule.rule_id}`}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    onEditApprove(rule, editText.trim());
                  }}
                  disabled={disabled || !editText.trim()}
                  data-testid={`cpl-edit-approve-submit-${rule.rule_id}`}
                >
                  Save & Approve
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onReject(rule)}
                  disabled={disabled}
                  data-testid={`cpl-reject-btn-${rule.rule_id}`}
                >
                  <XCircle className="h-4 w-4 mr-1" /> Reject
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditText(rule.rule_text || "");
                    setEditing(true);
                  }}
                  disabled={disabled}
                  data-testid={`cpl-edit-btn-${rule.rule_id}`}
                >
                  <Edit3 className="h-4 w-4 mr-1" /> Edit
                </Button>
                <Button
                  size="sm"
                  onClick={() => onApprove(rule)}
                  disabled={disabled}
                  data-testid={`cpl-approve-btn-${rule.rule_id}`}
                >
                  <Check className="h-4 w-4 mr-1" /> Approve
                </Button>
              </>
            )
          ) : rule.status === "approved" ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDeactivate(rule)}
              disabled={disabled}
              data-testid={`cpl-deactivate-btn-${rule.rule_id}`}
            >
              Deactivate rule
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ReviewDialog({
  open, doc, candidates, loading, onClose, onAction, disabled,
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="cpl-review-dialog">
        <DialogHeader>
          <DialogTitle>{doc ? doc.safe_filename : "Review rules"}</DialogTitle>
          <DialogDescription>
            AI-extracted rules are suggestions and do not become active until you
            approve them. Your organization remains responsible for reviewing its
            compliance requirements.
          </DialogDescription>
        </DialogHeader>
        {doc ? <TruncationBanner doc={doc} /> : null}
        <div className="max-h-[60vh] overflow-y-auto space-y-3 pr-1">
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-[color:var(--text-2)]" />
            </div>
          ) : candidates.length === 0 ? (
            <div className="text-center py-6 text-sm text-[color:var(--text-3)]">
              No rules extracted from this document yet.
            </div>
          ) : (
            candidates.map((c) => (
              <CandidateCard
                key={c.rule_id}
                rule={c}
                disabled={disabled}
                onApprove={(r) => onAction("approve", r)}
                onEditApprove={(r, text) => onAction("edit-and-approve", r, { approved_rule_text: text })}
                onReject={(r) => onAction("reject", r)}
                onDeactivate={(r) => onAction("deactivate", r)}
              />
            ))
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="cpl-review-close-btn">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConflictDialog({ conflict, onClose, onEdit }) {
  if (!conflict) return null;
  return (
    <Dialog open={!!conflict} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="cpl-conflict-dialog">
        <DialogHeader>
          <DialogTitle>Policy conflict detected</DialogTitle>
          <DialogDescription>
            {conflict.message ||
              "This rule would contradict an existing rule in your policy."}
          </DialogDescription>
        </DialogHeader>
        {conflict.conflicting_text ? (
          <div className="text-xs text-[color:var(--text-2)] italic border border-white/10 rounded p-2 break-words">
            "{conflict.conflicting_text}"
          </div>
        ) : null}
        <div className="text-[11px] text-[color:var(--text-3)]">
          This rule was NOT activated. Postmoro's active policy has not changed.
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="cpl-conflict-cancel-btn">
            Cancel
          </Button>
          <Button onClick={onEdit} data-testid="cpl-conflict-edit-btn">
            Edit rule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmDialog({
  open, title, description, confirmLabel = "Confirm", danger = false, onCancel,
  onConfirm, testIdPrefix = "confirm",
}) {
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid={`${testIdPrefix}-cancel-btn`}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            className={danger ? "bg-red-500 hover:bg-red-600 text-white" : undefined}
            data-testid={`${testIdPrefix}-confirm-btn`}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ActivePolicyView({ documents, effective }) {
  const approvedByDoc = useMemo(() => {
    // Collate approved rules across all documents so users can see
    // exactly what Postmoro is enforcing right now.
    const out = [];
    for (const d of documents) {
      // Fetch of the actual approved rules is handled where this
      // component is used (parent fetches once and passes through).
      const rows = d._approvedRules || [];
      for (const r of rows) {
        out.push({ ...r, _doc: d });
      }
    }
    return out;
  }, [documents]);

  return (
    <div
      className="rounded-lg border border-white/10 p-3 mt-4"
      data-testid="cpl-active-policy-view"
    >
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-emerald-300" />
        <h4 className="text-sm font-semibold text-[color:var(--text-1)]">
          Active Compliance Rules
        </h4>
      </div>
      <div className="mt-2 text-[11px] text-[color:var(--text-3)]">
        What Postmoro is enforcing right now.
      </div>

      <div className="mt-3 grid gap-2">
        <div className="text-[11px] uppercase tracking-wide text-[color:var(--text-3)]">
          Manual rules
        </div>
        {effective ? (
          <>
            {effective.prohibited_claims ? (
              <div className="text-xs text-[color:var(--text-1)] whitespace-pre-wrap">
                <b>Prohibited:</b>
                <div>{effective.prohibited_claims}</div>
              </div>
            ) : null}
            {effective.required_disclosures ? (
              <div className="text-xs text-[color:var(--text-1)] whitespace-pre-wrap">
                <b>Required disclosures:</b>
                <div>{effective.required_disclosures}</div>
              </div>
            ) : null}
            {effective.additional_restrictions ? (
              <div className="text-xs text-[color:var(--text-1)] whitespace-pre-wrap">
                <b>Additional restrictions:</b>
                <div>{effective.additional_restrictions}</div>
              </div>
            ) : null}
            {!effective.prohibited_claims && !effective.required_disclosures
              && !effective.additional_restrictions ? (
              <div className="text-xs text-[color:var(--text-3)]">
                No manual restrictions set.
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      <div className="mt-4 grid gap-2">
        <div className="text-[11px] uppercase tracking-wide text-[color:var(--text-3)]">
          Document-derived rules
        </div>
        {approvedByDoc.length === 0 ? (
          <div
            className="text-xs text-[color:var(--text-3)]"
            data-testid="cpl-active-empty"
          >
            No approved document rules yet.
          </div>
        ) : (
          approvedByDoc.map((r) => (
            <div
              key={r.rule_id}
              className="text-xs text-[color:var(--text-1)] border border-white/10 rounded p-2"
              data-testid={`cpl-active-rule-${r.rule_id}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline">
                  {CATEGORY_LABELS[r.category] || r.category}
                </Badge>
                {UNSUPPORTED_ENFORCEMENT.has(r.category) ? (
                  <Badge variant="outline">Manual follow-up</Badge>
                ) : null}
              </div>
              <div className="mt-1 whitespace-pre-wrap break-words">
                {r.approved_rule_text || r.rule_text}
              </div>
              <div className="mt-1 text-[11px] text-[color:var(--text-3)]">
                Source: {r._doc?.safe_filename || "Uploaded policy"}
                {r.source_page ? ` · Page ${r.source_page}` : ""}
                {r.source_section ? ` · Section: ${r.source_section}` : ""}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function CompliancePolicyLibrary({ effectiveCr }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [reviewDoc, setReviewDoc] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [candLoading, setCandLoading] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [conflictRuleId, setConflictRuleId] = useState(null);
  const [confirm, setConfirm] = useState(null); // {kind, doc?, rule?}
  const [actionBusy, setActionBusy] = useState(false);
  const [supersedeTarget, setSupersedeTarget] = useState(null); // doc being replaced
  const uploadInputRef = useRef(null);

  const refreshList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/compliance/documents");
      const docs = data?.documents || [];
      // Load approved rules for each document lazily so the Active
      // Policy View reflects real state.
      const enriched = await Promise.all(docs.map(async (d) => {
        if ((d.approved_rule_count || 0) === 0) return { ...d, _approvedRules: [] };
        try {
          const r = await api.get(
            `/compliance/documents/${d.document_id}/candidates`,
          );
          const approved = (r?.data?.rules || []).filter(
            (x) => x.status === "approved",
          );
          return { ...d, _approvedRules: approved };
        } catch {
          return { ...d, _approvedRules: [] };
        }
      }));
      setDocuments(enriched);
    } catch (e) {
      toast.error("Couldn't load your policy documents.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  const doUpload = useCallback(async (file, opts = {}) => {
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/compliance/documents", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.status === "duplicate") {
        toast.success("This document was already uploaded — showing existing copy.");
        // NEVER auto-extract on a duplicate: it would silently replace
        // the existing review state (including any historical rows).
      } else {
        toast.success("Policy uploaded.");
        // Fire-and-forget extraction on a brand-new document only.
        if (data?.text_extraction_status === "ok" && (data?.chunk_count || 0) > 0) {
          api.post(`/compliance/documents/${data.document_id}/extract`)
            .then(() => refreshList())
            .catch(() => { refreshList(); });
        }
      }
      // If this upload was chosen as a new version of an existing doc,
      // link them server-side via supersede.
      if (opts.supersedes && data?.document_id
          && data.document_id !== opts.supersedes) {
        try {
          await api.post(
            `/compliance/documents/${opts.supersedes}/supersede`,
            { new_document_id: data.document_id },
          );
        } catch (e) {
          // supersede is best-effort here — surface but don't fail upload.
          toast.warning("Uploaded, but couldn't mark previous version as superseded.");
        }
      }
      await refreshList();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? { file_too_large: "File is larger than 10 MB.",
            empty_file: "That file is empty.",
            pdf_signature_missing: "That doesn't look like a valid PDF file.",
            docx_contains_macros: "DOCX files with macros aren't accepted.",
            txt_contains_null_bytes: "The file appears to be binary, not plain text.",
            unsupported_type: "Only PDF, DOCX, or TXT files are supported." }[detail] || detail
        : "Upload failed. Please try again.";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  }, [refreshList]);

  const onExtract = useCallback(async (doc) => {
    setActionBusy(true);
    toast.loading("Analyzing policy…", { id: `extract-${doc.document_id}` });
    try {
      const { data } = await api.post(
        `/compliance/documents/${doc.document_id}/extract`,
      );
      toast.dismiss(`extract-${doc.document_id}`);
      if (data?.rule_extraction_status === "extraction_failed") {
        toast.error("We couldn't finish analyzing this document.");
      } else if (data?.rule_extraction_partial) {
        toast.warning("Analyzed with some sections skipped. Review carefully.");
      } else {
        toast.success("Analysis complete.");
      }
      await refreshList();
    } catch (e) {
      toast.dismiss(`extract-${doc.document_id}`);
      toast.error("Analysis failed. Please try again.");
    } finally {
      setActionBusy(false);
    }
  }, [refreshList]);

  const openReview = useCallback(async (doc) => {
    setReviewDoc(doc);
    setCandLoading(true);
    try {
      const { data } = await api.get(
        `/compliance/documents/${doc.document_id}/candidates`,
      );
      setCandidates(data?.rules || []);
    } catch {
      toast.error("Couldn't load the candidate rules for this document.");
      setCandidates([]);
    } finally {
      setCandLoading(false);
    }
  }, []);

  const reloadCandidates = useCallback(async () => {
    if (!reviewDoc) return;
    try {
      const { data } = await api.get(
        `/compliance/documents/${reviewDoc.document_id}/candidates`,
      );
      setCandidates(data?.rules || []);
    } catch {/* no-op */}
  }, [reviewDoc]);

  const onDownload = useCallback((doc) => {
    // Streaming download via the browser — server enforces cookie auth.
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const url = `${base}/api/compliance/documents/${doc.document_id}/download`;
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  const runRuleAction = useCallback(async (kind, rule, extra) => {
    setActionBusy(true);
    try {
      let resp;
      if (kind === "approve") {
        resp = await api.post(`/compliance/rules/${rule.rule_id}/approve`);
        toast.success("Rule approved.");
      } else if (kind === "edit-and-approve") {
        resp = await api.post(
          `/compliance/rules/${rule.rule_id}/edit-and-approve`,
          extra,
        );
        toast.success("Rule approved.");
      } else if (kind === "reject") {
        resp = await api.post(`/compliance/rules/${rule.rule_id}/reject`);
        toast.success("Rule rejected. It won't be used in compliance checks.");
      } else if (kind === "deactivate") {
        resp = await api.post(`/compliance/rules/${rule.rule_id}/deactivate`);
        toast.success("Rule deactivated.");
      }
      await Promise.all([reloadCandidates(), refreshList()]);
      return resp;
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail && typeof detail === "object"
          && detail.code === "policy_rule_conflict") {
        setConflict(detail);
        setConflictRuleId(rule.rule_id);
      } else if (status === 409 && detail && typeof detail === "object"
                 && detail.code === "invalid_rule_transition") {
        toast.error(`This rule can't move from ${detail.from} to ${detail.to}.`);
      } else if (status === 409 && detail?.code === "policy_update_conflict") {
        toast.error("Your policy is being updated concurrently — please retry.");
      } else {
        toast.error("Action failed. Please try again.");
      }
    } finally {
      setActionBusy(false);
    }
  }, [reloadCandidates, refreshList]);

  const handleReviewAction = useCallback((kind, rule, extra) => {
    if (kind === "approve") {
      setConfirm({
        kind: "approve", rule,
        title: "Approve this rule?",
        description: "Once approved, Postmoro will apply it when checking generated and published content against your organization's saved content restrictions.",
        confirmLabel: "Approve",
      });
    } else if (kind === "deactivate") {
      setConfirm({
        kind: "deactivate", rule,
        title: "Deactivate this rule?",
        description: "This rule will no longer be applied to future compliance checks. Its history is preserved.",
        confirmLabel: "Deactivate",
        danger: true,
      });
    } else if (kind === "reject") {
      setConfirm({
        kind: "reject", rule,
        title: "Reject this rule?",
        description: "Rejected rules are not used when Postmoro checks content.",
        confirmLabel: "Reject",
      });
    } else if (kind === "edit-and-approve") {
      // No extra confirmation — the user typed edited text explicitly.
      runRuleAction("edit-and-approve", rule, extra);
    }
  }, [runRuleAction]);

  const onConfirm = useCallback(async () => {
    if (!confirm) return;
    const c = confirm;
    setConfirm(null);
    await runRuleAction(c.kind, c.rule, c.extra);
  }, [confirm, runRuleAction]);

  const onDeleteDoc = useCallback(async (doc, blocked) => {
    if (blocked) {
      toast.error("This document has active compliance rules. Deactivate or supersede those rules before deleting the document.");
      return;
    }
    if (!window.confirm(`Delete "${doc.safe_filename}"? This can't be undone.`)) return;
    try {
      await api.delete(`/compliance/documents/${doc.document_id}`);
      toast.success("Document deleted.");
      await refreshList();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object"
          && detail.code === "document_has_active_rules") {
        toast.error("This document has active compliance rules. Deactivate or supersede those rules before deleting the document.");
      } else {
        toast.error("Delete failed. Please try again.");
      }
    }
  }, [refreshList]);

  const onUploadNewVersion = useCallback((doc) => {
    setSupersedeTarget(doc);
    uploadInputRef.current?.click();
  }, []);

  const handleNewVersionFile = useCallback((files) => {
    const f = files && files[0];
    if (!f || !supersedeTarget) return;
    if (f.size > MAX_BYTES) {
      toast.error("File is larger than 10 MB.");
      setSupersedeTarget(null);
      return;
    }
    doUpload(f, { supersedes: supersedeTarget.document_id });
    setSupersedeTarget(null);
  }, [doUpload, supersedeTarget]);

  const activeDocs = documents.filter((d) => d.status !== "superseded");
  const supersededDocs = documents.filter((d) => d.status === "superseded");

  return (
    <div className="mt-6" data-testid="compliance-policy-library">
      <div className="border-t border-white/10 pt-5">
        <h3 className="text-base font-semibold text-[color:var(--text-1)]">
          Compliance Policy Library
        </h3>
        <p className="text-xs text-[color:var(--text-2)] mt-1">
          Upload your organization's policies and guidelines. Postmoro can
          identify potential content restrictions and disclosures for your review.
        </p>
        <p className="text-[11px] text-[color:var(--text-3)] mt-1">
          AI-extracted rules are suggestions and do not become active until you
          approve them. Your organization remains responsible for reviewing its
          compliance requirements.
        </p>
      </div>

      <div className="mt-4">
        <Dropzone onFile={doUpload} uploading={uploading} />
        {/* Hidden new-version input triggered from row action */}
        <input
          ref={uploadInputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => handleNewVersionFile(e.target.files)}
          data-testid="cpl-new-version-input"
        />
      </div>

      <div className="mt-5 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-[color:var(--text-2)]" />
          </div>
        ) : documents.length === 0 ? (
          <EmptyState onBrowse={() => document
            .querySelector('[data-testid="cpl-file-input"]')?.click()} />
        ) : (
          <>
            {activeDocs.map((d) => (
              <DocumentRow
                key={d.document_id}
                doc={d}
                existingIds={activeDocs.map((x) => x.document_id)}
                onExtract={onExtract}
                onReview={openReview}
                onDownload={onDownload}
                onDelete={onDeleteDoc}
                onUploadNewVersion={onUploadNewVersion}
              />
            ))}
            {supersededDocs.length > 0 ? (
              <div className="mt-4">
                <div
                  className="text-[11px] uppercase tracking-wide text-[color:var(--text-3)] mb-2"
                  data-testid="cpl-superseded-section"
                >
                  Superseded versions
                </div>
                {supersededDocs.map((d) => (
                  <DocumentRow
                    key={d.document_id}
                    doc={d}
                    existingIds={activeDocs.map((x) => x.document_id)}
                    onExtract={onExtract}
                    onReview={openReview}
                    onDownload={onDownload}
                    onDelete={onDeleteDoc}
                    onUploadNewVersion={onUploadNewVersion}
                  />
                ))}
              </div>
            ) : null}
          </>
        )}
      </div>

      <ActivePolicyView documents={documents} effective={effectiveCr} />

      <ReviewDialog
        open={!!reviewDoc}
        doc={reviewDoc}
        candidates={candidates}
        loading={candLoading}
        disabled={actionBusy}
        onClose={() => { setReviewDoc(null); setCandidates([]); }}
        onAction={handleReviewAction}
      />

      <ConflictDialog
        conflict={conflict}
        onClose={() => setConflict(null)}
        onEdit={() => {
          // Bring user's focus back to the review dialog where they can
          // edit the conflicting rule.
          setConflict(null);
          // If the review dialog is already open the CandidateCard's
          // "Edit" button drives editing; just close the conflict.
        }}
      />

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title || ""}
        description={confirm?.description || ""}
        confirmLabel={confirm?.confirmLabel || "Confirm"}
        danger={!!confirm?.danger}
        testIdPrefix={
          confirm ? `cpl-${confirm.kind}-confirm` : "cpl-confirm"
        }
        onCancel={() => setConfirm(null)}
        onConfirm={onConfirm}
      />
    </div>
  );
}
